import z from "zod/v4";
import { TenantId } from "./tenant-id";
import { SiteId } from "./site-id";
import { AssetId } from "./asset-id";
import type { InstalledAsset } from "./installed-asset";

// Local codec definitions to avoid circular dependency with codecs.ts
const TenantIdCodec = z.codec(z.string(), z.custom<TenantId>(), {
  decode: (s) => TenantId.fromString(s),
  encode: (id) => TenantId.toString(id),
});

const SiteIdCodec = z.codec(z.string(), z.custom<SiteId>(), {
  decode: (s) => SiteId.fromString(s),
  encode: (id) => SiteId.toString(id),
});

const AssetIdCodec = z.codec(z.string(), z.custom<AssetId>(), {
  decode: (s) => AssetId.fromString(s),
  encode: (id) => AssetId.toString(id),
});

const InstalledAssetObjectSchema = z.object({
  tenant: TenantIdCodec,
  site: SiteIdCodec,
  asset: AssetIdCodec,
});

/**
 * Codec for topic format: `{tenant}/{site}/{asset}`
 *
 * Example: "T2277CC8/S0-0/A2-G2-CAM_11241"
 */
export const InstalledAssetInputTopic = z.codec(z.string(), z.custom<InstalledAsset>(), {
  decode: (str, ctx) => {
    const parts = str.split("/");
    const [tenantStr, siteStr, assetStr, ...rest] = parts;
    if (tenantStr === undefined || siteStr === undefined || assetStr === undefined || rest.length > 0) {
      ctx.issues.push({
        code: "custom",
        message: `Expected topic format: {tenant}/{site}/{asset}, got ${String(parts.length)} parts`,
        input: str,
      });
      return z.NEVER;
    }

    try {
      return {
        tenant: TenantId.fromString(tenantStr),
        site: SiteId.fromString(siteStr),
        asset: AssetId.fromString(assetStr),
      };
    } catch (err: unknown) {
      ctx.issues.push({
        code: "custom",
        message: err instanceof Error ? err.message : "Invalid topic format",
        input: str,
      });
      return z.NEVER;
    }
  },
  encode: (installed) =>
    `${TenantId.toString(installed.tenant)}/${SiteId.toString(installed.site)}/${AssetId.toString(installed.asset)}`,
});

/**
 * Codec for URL format: `tenants/{tenant}/sites/{site}/assets/{asset}`
 *
 * Example: "tenants/T2277CC8/sites/S0-0/assets/A2-G2-CAM_11241"
 */
export const InstalledAssetInputUrl = z.codec(z.string(), z.custom<InstalledAsset>(), {
  decode: (str, ctx) => {
    const match = str.match(/^tenants\/([^/]+)\/sites\/([^/]+)\/assets\/([^/]+)$/);
    const tenantStr = match?.[1];
    const siteStr = match?.[2];
    const assetStr = match?.[3];
    if (tenantStr === undefined || siteStr === undefined || assetStr === undefined) {
      ctx.issues.push({
        code: "custom",
        message: "Expected URL format: tenants/{tenant}/sites/{site}/assets/{asset}",
        input: str,
      });
      return z.NEVER;
    }

    try {
      return {
        tenant: TenantId.fromString(tenantStr),
        site: SiteId.fromString(siteStr),
        asset: AssetId.fromString(assetStr),
      };
    } catch (err: unknown) {
      ctx.issues.push({
        code: "custom",
        message: err instanceof Error ? err.message : "Invalid URL format",
        input: str,
      });
      return z.NEVER;
    }
  },
  encode: (installed) =>
    `tenants/${TenantId.toString(installed.tenant)}/sites/${SiteId.toString(installed.site)}/assets/${AssetId.toString(installed.asset)}`,
});

/**
 * Codec for positional tuple format: [tenant, site, asset]
 *
 * Example: ["T2277CC8", "S0-0", "A2-G2-CAM_11241"]
 */
export const InstalledAssetInputTuple = z.codec(
  z.tuple([TenantIdCodec, SiteIdCodec, AssetIdCodec]),
  z.custom<InstalledAsset>(),
  {
    decode: (tuple) => ({
      tenant: tuple[0],
      site: tuple[1],
      asset: tuple[2],
    }),
    encode: (installed): [TenantId, SiteId, AssetId] => [installed.tenant, installed.site, installed.asset],
  },
);

/**
 * Discriminated codec that accepts any of the supported input formats
 * and decodes to InstalledAsset.
 *
 * Supported formats:
 * 1. Object: `{ tenant, site, asset }`
 * 2. Tuple: `[tenant, site, asset]`
 * 3. Topic string: `{tenant}/{site}/{asset}`
 * 4. URL string: `tenants/{tenant}/sites/{site}/assets/{asset}`
 *
 * @example
 * // Object format
 * InstalledAssetInput.parse({ tenant: "T2277CC8", site: "S0-0", asset: "A2-G2-CAM_11241" })
 *
 * @example
 * // Tuple format
 * InstalledAssetInput.parse(["T2277CC8", "S0-0", "A2-G2-CAM_11241"])
 *
 * @example
 * // Topic format
 * InstalledAssetInput.parse("T2277CC8/S0-0/A2-G2-CAM_11241")
 *
 * @example
 * // URL format
 * InstalledAssetInput.parse("tenants/T2277CC8/sites/S0-0/assets/A2-G2-CAM_11241")
 */
export const InstalledAssetInput = z.codec(
  z.union([
    z.object({ tenant: TenantIdCodec, site: SiteIdCodec, asset: AssetIdCodec }),
    z.tuple([TenantIdCodec, SiteIdCodec, AssetIdCodec]),
    z.string(),
  ]),
  z.custom<InstalledAsset>(),
  {
    decode: (input, ctx) => {
      // Object format (non-array object)
      if (typeof input === "object" && !Array.isArray(input)) {
        const result = InstalledAssetObjectSchema.safeParse(input);
        if (!result.success) {
          ctx.issues.push({
            code: "custom",
            message: result.error.issues.map((i) => i.message).join("; "),
            input,
          });
          return z.NEVER;
        }
        return result.data;
      }

      // Tuple format
      if (Array.isArray(input)) {
        const result = InstalledAssetInputTuple.safeParse(input);
        if (!result.success) {
          ctx.issues.push({
            code: "custom",
            message: result.error.issues.map((i) => i.message).join("; "),
            input,
          });
          return z.NEVER;
        }
        return result.data;
      }

      // String formats - try URL first (more specific), then topic
      if (typeof input === "string") {
        if (input.startsWith("tenants/")) {
          const result = InstalledAssetInputUrl.safeParse(input);
          if (!result.success) {
            ctx.issues.push({
              code: "custom",
              message: result.error.issues.map((i) => i.message).join("; "),
              input,
            });
            return z.NEVER;
          }
          return result.data;
        }

        // Topic format (3 parts separated by /)
        const result = InstalledAssetInputTopic.safeParse(input);
        if (!result.success) {
          ctx.issues.push({
            code: "custom",
            message: result.error.issues.map((i) => i.message).join("; "),
            input,
          });
          return z.NEVER;
        }
        return result.data;
      }

      ctx.issues.push({
        code: "custom",
        message: "Expected object, tuple, topic string, or URL string",
        input,
      });
      return z.NEVER;
    },
    encode: (installed) => ({
      tenant: installed.tenant,
      site: installed.site,
      asset: installed.asset,
    }),
  },
);

/**
 * Input type for InstalledAssetInput codec.
 *
 * Accepts:
 * - Object: `{ tenant, site, asset }` (with branded or string types)
 * - Tuple: `[tenant, site, asset]`
 * - Topic string: `{tenant}/{site}/{asset}`
 * - URL string: `tenants/{tenant}/sites/{site}/assets/{asset}`
 */
export type InstalledAssetInput = z.input<typeof InstalledAssetInput>;
